import { useMutation } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useCallback } from 'react'
import type { AdminModel } from 'sunlife-domain'
import { computeStandardProductHashes, createAdminRecipe, domainFactory } from 'sunlife-domain'

import { AuthorizedClient } from '@salsita-ai/platform-project-sdk/client'
import { getIdSequence } from '@salsita-ai/platform-project-sdk/domain'
import { useLogger, useSessionService } from '@salsita-ai/platform-project-sdk/react'

import { adminInterface } from '../systems/admin-interface'

export const prePublishAdminHandler = adminInterface.createPrePublishAdminHandler(() => {
  const { requestSessionToken } = useSessionService()

  const $admin = adminInterface.useRootEntityState()
  const { storeSystem } = adminInterface.useDomain()
  const logger = useLogger()

  const { enqueueSnackbar } = useSnackbar()

  const { mutateAsync } = useMutation({
    mutationFn: async () => {
      return Promise.all(
        $admin.$presets
          .filter(
            preset => preset.tags.includes('standard-price') && preset.visible && preset.$category?.visible === true
          )
          .filter(preset => preset.configurationId && preset.revisionId && preset.readToken)
          .map(async preset => {
            const authorizedApiClient = new AuthorizedClient(
              phoenix.runtimeConfig.SERVER_URL,
              preset.readToken!,
              requestSessionToken
            )

            const revision = await authorizedApiClient.getConfigurationRevision(
              preset.configurationId!,
              preset.revisionId!
            )

            const entities = domainFactory.validateAndMigrateState(revision.data, revision.modelVersion)

            return entities
          })
      )
    },
    onError: error => {
      logger.error('Error updating standard products', undefined, error)
      enqueueSnackbar('Error updating standard products', { variant: 'error' })
    },
    onSuccess: states => {
      const sequence = getIdSequence<AdminModel.AnyDomainEntity>([], [])
      const standardProducts = states.map(state =>
        createAdminRecipe({
          id: sequence.nextId('StandardProduct'),
          type: 'StandardProduct',
          entityHashes: computeStandardProductHashes(state),
        })
      )

      const updates = [
        ...$admin.$standardProducts.map(standardProduct =>
          adminInterface.updates.EntityDeletion({
            entityId: standardProduct.id,
            updateTag: 'cleanupExistingStandardProducts',
          })
        ),
        ...standardProducts.map(standardProduct => {
          return adminInterface.updates.DomainEntityCreation({
            entity: standardProduct,
            updateTag: 'createNewStandardProducts',
            parentId: $admin.id,
          })
        }),
      ]

      storeSystem.stageAndCommit(updates)
    },
  })

  return {
    handler: useCallback(async () => {
      await mutateAsync()
    }, [mutateAsync]),
  }
})
